import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, filter, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ProductCategoryService } from 'src/app/shared/store/product-category/product-category.service';
import {
  ProductCategoryDocumentType,
  ProductCategoryModel,
} from 'src/app/shared/store/product-category/types';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ActionButton } from '../../../../shared/components/modal-content/action-button';
import { ModalContentComponent } from '../../../../shared/components/modal-content/modal-content.component';
import { dynamicSort } from '../../../../shared/helper/arrayhelper';
import { toFirstLetterLower } from '../../../../shared/helper/stringhelper';
import { ModalService } from '../../../../shared/service/modal.service';
import {
  DocumentTypeExtendedModel,
  DocumentTypeModel,
} from '../../../../shared/store/document-type/types';
import { FormService } from '../../../../shared/store/form/form.service';
import { SubHeaderService } from '../../../../shared/store/subheader/subheader.service';
import {
  DataType,
  DropdownStyle,
  SourceEnum,
} from '../../../../shared/store/typeahead/types';
import { View } from '../../../../shared/store/view/types';
import { ViewService } from '../../../../shared/store/view/views.service';
import { AmountType } from '../../../../shared/store/commissions/commission-plan-rule/types';
import { getAmountSign } from '../../../../shared/helper/amounthelper';
import {
  ClaimTypeDocumentTypeModel,
  ClaimTypeModel,
} from '../../../../shared/store/claim-type/types';
import { ModalComponentKey } from 'src/app/shared/service/modal-component';

@UntilDestroy()
@Component({
  selector: 'app-product-category-edit',
  templateUrl: './product-category-edit.component.html',
  styleUrls: ['./product-category-edit.component.scss'],
  standalone: false,
})
export class ProductCategoryEditComponent implements OnInit, OnDestroy {
  view$: Observable<View>;
  productCategoryForm: FormGroup;
  productCategory$: Observable<ProductCategoryModel>;

  defaultAmountType: AmountType = 'Monetary';
  amountPlaceholder: string;
  amountSuffix: string;

  protected readonly DataType = DataType;
  protected readonly DropdownStyle = DropdownStyle;

  constructor(
    private readonly subHeaderService: SubHeaderService,
    private readonly productCategoryService: ProductCategoryService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly formService: FormService,
    private readonly formBuilder: FormBuilder,
    private readonly modalService: NgbModal,
    private readonly modalExchangeService: ModalService,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.productCategory$ = this.productCategoryService.getById.value$;
  }

  ngOnInit(): void {
    //Initialisation du suffixe et du placeholder
    this.amountPlaceholder = this.amountSuffix = getAmountSign(
      this.defaultAmountType,
    );

    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'productCategoryUpdate') this.onSubmit();
        if (x == 'productCategoryDetails') {
          this.formService.clear();
          this.router.navigate([
            '/Insurance/ProductCategories/Details',
            this.route.snapshot.params['id'],
          ]);
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.productCategoryService.getById.call(this.route.snapshot.params['id']);

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
      });

    this.productCategoryForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      description: [''],
      productCategoryType: [1, [Validators.required]],
      productCategoryEnumKey: [],
      sequence: [99],
      icon: [''],
      productCategoryDocumentTypes: this.formBuilder.array([]),
      defaultTaxRate: [0, [Validators.required]],
      code: ['', [Validators.required, Validators.maxLength(3)]],
      claimTypes: this.formBuilder.array([]),
    });

    combineLatest([this.productCategory$, this.view$])
      .pipe(
        untilDestroyed(this),
        filter(([productCategory, view]) => !!productCategory && !!view),
      )
      .subscribe(([x, view]) => {
        this.breadcrumbService.set('@productCategoryEdit', x.name);
        this.productCategoryForm.patchValue({
          name: x.name,
          description: x.description,
          productCategoryType: x.productCategoryType,
          productCategoryEnumKey: x.productCategoryEnumKey,
          sequence: x.sequence,
          icon: x.icon,
          defaultTaxRate: x.defaultTaxRate,
          code: x.code,
        });
        const existingProductCategoryDocumentTypes =
          x.productCategoryDocumentTypes;
        let patchProductCategoryDocumentTypes: any[] = [];
        this.productCategoryDocumentTypes.clear();
        for (let i = 0; i < existingProductCategoryDocumentTypes.length; i++) {
          this.addProductCategoryDocumentType();
          patchProductCategoryDocumentTypes.push({
            documentTypeId:
              existingProductCategoryDocumentTypes[i].documentTypeId,
            documentTypeName:
              existingProductCategoryDocumentTypes[i].documentTypeName,
            isMandatory: existingProductCategoryDocumentTypes[i].mandatory,
            documentFlow: existingProductCategoryDocumentTypes[i].documentFlow,
          });
        }
        this.productCategoryDocumentTypes.patchValue(
          patchProductCategoryDocumentTypes,
        );

        this.claimTypes.clear();
        this.sortClaimTypesLikeGrid(x.claimTypes ?? [], view).forEach(
          (claimType: ClaimTypeModel) => {
            this.claimTypes.push(this.buildClaimTypeFormGroup(claimType));
          },
        );
      });
  }

  /**
   * Reproduces the sort applied by the details grid so both pages list the claim types in the same
   * order: the 'claimTypes' tab view drives it through its defaultSort column and orderBy.
   */
  private sortClaimTypesLikeGrid(
    claimTypes: ClaimTypeModel[],
    view: View,
  ): ClaimTypeModel[] {
    const treeView = view.tabViews?.find(
      (x) => x.name === 'claimTypes',
    )?.treeView;
    if (!treeView?.defaultSort) return claimTypes;
    const defaultSort = treeView.defaultSort;
    // The grid only sorts when a column carries that name, and only for asc/desc.
    if (!treeView.columns?.some((column) => column.name === defaultSort))
      return claimTypes;
    if (treeView.orderBy !== 'asc' && treeView.orderBy !== 'desc')
      return claimTypes;

    return [...claimTypes].sort(
      dynamicSort(
        treeView.orderBy === 'desc' ? `-${defaultSort}` : defaultSort,
      ),
    );
  }

  onSubmit() {
    if (this.productCategoryForm.invalid) {
      this.productCategoryForm.markAllAsTouched();
      this.formService.setEntityErrors('PRODUCT_CATEGORY');
      this.formService.countErrors(this.productCategoryForm, true);
      return;
    }

    let productCategoryDocumentTypes: ProductCategoryDocumentType[] = [];

    this.productCategoryForm.value.productCategoryDocumentTypes.forEach((x) => {
      const productCategoryDocumentType: ProductCategoryDocumentType = {
        productCategoryId: this.route.snapshot.params['id'],
        documentTypeId: x.documentTypeId,
        documentFlow: x.documentFlow,
        mandatory: x.isMandatory,

        //Others
        id: 0,
        documentTypeName: x.documentTypeName,
        documentTypeDescription: '',
        productCategoryName: '',
      };
      productCategoryDocumentTypes.push(productCategoryDocumentType);
    });

    let claimTypes: ClaimTypeModel[] = [];
    this.productCategoryForm.value.claimTypes.forEach((x) => {
      const claimTypeDocumentTypes: ClaimTypeDocumentTypeModel[] = (
        x.claimTypeDocumentTypes ?? []
      ).map((document) => ({
        id: document.id ?? 0,
        claimTypeId: x.claimTypeId ?? 0,
        documentTypeId: document.documentTypeId,
        mandatory: document.mandatory,

        //Others
        claimTypeName: '',
        documentTypeName: document.documentTypeName ?? '',
      }));

      const claimType: ClaimTypeModel = {
        id: x.claimTypeId,
        name: x.claimTypeName,
        productCategoryId: this.route.snapshot.params['id'],
        sequence: x.claimTypeSequence,
        active: x.claimTypeActive,
        defaultFeeAmount: x.claimTypeDefaultFeeAmount,
        claimTypeDocumentTypes: claimTypeDocumentTypes,

        //Others
        productCategoryName: '',
      };
      claimTypes.push(claimType);
    });

    const productCategoryModel: ProductCategoryModel = {
      id: this.route.snapshot.params['id'],
      name: this.productCategoryForm.value.name!,
      description: this.productCategoryForm.value.description!,
      productCategoryType: this.productCategoryForm.value.productCategoryType!,
      productCategoryEnumKey:
        this.productCategoryForm.value.productCategoryEnumKey!,
      sequence: this.productCategoryForm.value.sequence!,
      icon: this.productCategoryForm.value.icon!,
      code: this.productCategoryForm.value.code!,

      productCategoryDocumentTypes: productCategoryDocumentTypes,
      claimTypes: claimTypes,
      //Others
      productCategoryTypeName: '',
      insuranceCoverages: [],
      defaultTaxRate: this.productCategoryForm.value.defaultTaxRate!,
    };

    let result$ = this.productCategoryService.update.call(productCategoryModel);
    result$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((result) => {
        this.formService.clear();
        this.router.navigate([
          '/Insurance/ProductCategories/Details',
          result.id,
        ]);
      });
  }

  ngOnDestroy(): void {
    this.productCategoryService.update.reset();
    this.breadcrumbService.set('@productCategoryEdit', ' ');
  }

  openModalChoiceDocumentType() {
    let documentTypes: DocumentTypeModel[] = [];

    this.productCategoryForm.value.productCategoryDocumentTypes.forEach((x) => {
      const documentType: DocumentTypeModel = {
        id: x.documentTypeId,
        name: x.documentTypeName,
        description: '',
      };
      documentTypes.push(documentType);
    });

    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.SelectDocumentTypeEdit,
      );
    modalRef.componentInstance.title = 'Sélection des documents';
    modalRef.componentInstance.cancelButton = true;
    modalRef.componentInstance.buttons = [
      <ActionButton>{
        actionName: toFirstLetterLower('documentType') + 'Update',
      },
    ];
    modalRef.componentInstance.inputParams = {
      source: SourceEnum.Modal,
      selectedDocumentTypes: documentTypes,
    };

    // The selection comes back as the modal result; a dismiss rejects the promise, hence the catch.
    modalRef.result
      .then((selection: DocumentTypeModel[]) =>
        this.applyProductCategoryDocumentTypes(selection),
      )
      .catch(() => {});
  }

  /**
   * Rebuilds the product category document rows from the modal selection, keeping the flow and
   * mandatory flag already entered on the rows that survive the selection.
   */
  private applyProductCategoryDocumentTypes(selection: DocumentTypeModel[]) {
    const values = this.productCategoryForm.value.productCategoryDocumentTypes;

    const rows = selection.map((documentType) => {
      const existing = values.find(
        (pcdt) => pcdt.documentTypeId === documentType.id,
      );

      return (
        existing ?? {
          documentTypeId: documentType.id,
          documentTypeName: documentType.name,
          isMandatory: true,
          documentFlow: 'ToReceive',
        }
      );
    });

    this.productCategoryDocumentTypes.clear();
    for (let i = 0; i < rows.length; i++) {
      this.addProductCategoryDocumentType();
    }
    this.productCategoryDocumentTypes.patchValue(rows);
  }

  addProductCategoryDocumentType() {
    const productCategoryDocumentTypeForm = this.formBuilder.group({
      documentTypeId: [0],
      documentTypeName: [''],
      isMandatory: [true],
      documentFlow: ['ToReceive'],
    });

    this.productCategoryDocumentTypes.push(productCategoryDocumentTypeForm);
  }

  removeProductCategoryDocumentType(index: number) {
    this.productCategoryDocumentTypes.removeAt(index);
  }

  get productCategoryDocumentTypes() {
    return this.productCategoryForm.controls[
      'productCategoryDocumentTypes'
    ] as FormArray;
  }

  onBlurName($event: FocusEvent) {
    let formControlValue = this.productCategoryForm.get('code');
    const name = this.productCategoryForm.get('name')?.value;
    formControlValue?.setValue(name.slice(0, 3).toUpperCase());
  }

  get claimTypes() {
    return this.productCategoryForm.controls['claimTypes'] as FormArray;
  }

  removeClaimType(index: number) {
    this.claimTypes.removeAt(index);
  }

  addClaimType() {
    this.claimTypes.push(this.buildClaimTypeFormGroup(null));
  }

  buildClaimTypeFormGroup(claimType: ClaimTypeModel | null) {
    const documentRows = this.sortClaimTypeDocuments(
      claimType?.claimTypeDocumentTypes ?? [],
    ).map((document) => this.buildClaimTypeDocumentGroup(document));

    return this.formBuilder.group({
      claimTypeId: [claimType?.id ?? 0],
      claimTypeName: [claimType?.name ?? '', Validators.required],
      claimTypeActive: [claimType?.active ?? true],
      claimTypeSequence: [claimType?.sequence ?? 0],
      claimTypeDefaultFeeAmount: [claimType?.defaultFeeAmount ?? null],
      claimTypeDocumentTypes: this.formBuilder.array(documentRows),
      index: [this.claimTypes.length + 1],
    });
  }

  /** Linked-document rows of the claim type at the given index. */
  claimTypeDocuments(index: number): FormArray {
    return (this.claimTypes.at(index) as FormGroup).get(
      'claimTypeDocumentTypes',
    ) as FormArray;
  }
  /** Number of documents linked to the claim type at the given index. */
  selectedDocumentsCount(index: number): number {
    return this.claimTypeDocuments(index).length;
  }
  /**
   * Opens the modal to pick the document types (and their mandatory flag) for a claim type, then
   * replaces that claim type's document rows with the modal's result.
   */
  openClaimTypeDocumentsModal(index: number) {
    const documentTypes: DocumentTypeExtendedModel[] = this.claimTypeDocuments(
      index,
    ).value.map((x) => ({
      id: x.documentTypeId,
      name: x.documentTypeName,
      description: '',
      mandatory: x.mandatory,
    }));

    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.SelectClaimTypeDocumentTypeEdit,
      );
    modalRef.componentInstance.title = 'CLAIM_TYPE_DOCUMENT_TYPE_LBL_TITLE';
    modalRef.componentInstance.cancelButton = true;
    modalRef.componentInstance.buttons = [
      <ActionButton>{
        actionName: 'claimTypeDocumentTypesUpdate',
      },
    ];
    modalRef.componentInstance.inputParams = {
      source: SourceEnum.Modal,
      selectedDocumentTypes: documentTypes,
    };

    // The selection comes back as the modal result, so the targeted claim type is the one captured
    // by this closure. A dismiss rejects the promise, hence the catch.
    modalRef.result
      .then((selection: DocumentTypeExtendedModel[]) =>
        this.applyClaimTypeDocumentTypes(index, selection),
      )
      .catch(() => {});
  }

  /**
   * Rebuilds the document rows of the claim type at the given index from the modal selection. A
   * document that was already linked keeps its link id — the modal only knows document types —
   * while the modal wins on the mandatory flag.
   */
  private applyClaimTypeDocumentTypes(
    index: number,
    selection: DocumentTypeExtendedModel[],
  ) {
    const rows = this.claimTypeDocuments(index);
    const values = rows.value;

    const newRows = this.sortClaimTypeDocuments(
      selection.map((documentType) => {
        const existing = values.find(
          (ctdt) => ctdt.documentTypeId === documentType.id,
        );

        return {
          id: existing?.id ?? 0,
          documentTypeId: documentType.id,
          documentTypeName: documentType.name,
          mandatory: documentType.mandatory ?? existing?.mandatory ?? true,
        };
      }),
    );

    rows.clear();
    newRows.forEach(() => rows.push(this.buildClaimTypeDocumentGroup()));
    rows.patchValue(newRows);
  }

  /**
   * Claim type documents are listed mandatory first, then by name: the badges and the payload sent
   * on submit follow that same order.
   */
  private sortClaimTypeDocuments<
    T extends { mandatory?: boolean; documentTypeName?: string },
  >(documents: T[]): T[] {
    return [...documents].sort(
      (a, b) =>
        Number(b.mandatory) - Number(a.mandatory) ||
        (a.documentTypeName ?? '').localeCompare(b.documentTypeName ?? '', 'fr'),
    );
  }

  /** One document row of a claim type: the link id, the document type and its mandatory flag. */
  private buildClaimTypeDocumentGroup(
    document?: ClaimTypeDocumentTypeModel,
  ): FormGroup {
    return this.formBuilder.group({
      id: [document?.id ?? 0],
      documentTypeId: [document?.documentTypeId ?? 0],
      documentTypeName: [document?.documentTypeName ?? ''],
      mandatory: [document?.mandatory ?? true],
    });
  }
}
